import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useContext, useEffect, useState } from 'react';
import { LoginInfo } from '../App';
import { login, verifyTwoFactorCode, resendTwoFactorCode, getAntiPhishingPrefix } from "../service/LoginService";
import CampaignMessage from './CampaignMessage';
import Category from './Category';
import Dialog from './Dialog';
import Footer from './Footer';
import Header from './Header';
import { useTranslation } from "react-i18next";

import {
    Container,
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Link,
    IconButton,
    InputAdornment,
    CssBaseline,
} from '@mui/material';
import {
    Email,
    Lock,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import ApiFetch from './ApiFetch';

// カスタムスタイルの定義
const styles = {
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
    },
    mainContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
        zIndex: 1,
    },
    backgroundImage: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'opacity 0.5s ease-in-out',
        zIndex: 0,
    },
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.5)', // 白の半透明オーバーレイ
        zIndex: 0,
    },
    formContainer: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '24px',
        position: 'relative',
    },
    paper: {
        padding: 4,
        width: '100%',
        maxWidth: 400,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    form: {
        width: '100%',
        marginTop: 2,
    },
    submit: {
        margin: '24px 0 16px',
    },
    forgotPassword: {
        marginLeft: 2,
    }
};



const Login = () => {
    async function getImage() {
        return await new ApiFetch().get(ApiFetch.API_PROTOCOL + "//" + ApiFetch.API_HOST + "/v1/image/loginImage").then((r) => r.json().then((json) => {
            return json
        }))
    }
    const { t, i18n } = useTranslation()
    // ログイン情報
    const { loginInfo, setLoginInfo } = useContext(LoginInfo)
    const [currentBackground, setCurrentBackground] = useState('')
    const [loading, setLoading] = useState(false)

    // ダイアログ
    const [show, setShow] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogMessage, setDialogMessage] = useState("");
    function dialog(title, message) {
        setDialogTitle(title)
        setDialogMessage(message)
        setShow(true)
    }

    // 画像
    const [image, setImage] = useState(null)

    // フォームの状態管理
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    
    // 2要素認証の状態管理
    const [twoFactorRequired, setTwoFactorRequired] = useState(false);
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [antiPhishingPrefix, setAntiPhishingPrefix] = useState('');

    // 入力値の変更ハンドラ
    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    };

    // 2要素認証コード処理
    const handleTwoFactorSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        try {
            const result = await verifyTwoFactorCode(twoFactorCode);
            if (result.result) {
                setLoginInfo(result);
                window.location.href = "/";
            } else {
                dialog("エラー", result.errors || "認証に失敗しました。");
            }
        } catch (error) {
            console.error('Two-factor authentication failed:', error);
            dialog("エラー", "認証に失敗しました。");
        } finally {
            setLoading(false);
        }
    };

    // 2要素認証コード再送信
    const handleResendCode = async () => {
        setLoading(true);
        try {
            const result = await resendTwoFactorCode();
            if (result.result) {
                // 新しいアンチフィッシング接頭辞を更新
                if (result.anti_phishing_prefix) {
                    setAntiPhishingPrefix(result.anti_phishing_prefix);
                }
                dialog("送信完了", "認証コードを再送信しました。");
            } else {
                dialog("エラー", result.errors || "再送信に失敗しました。");
            }
        } catch (error) {
            console.error('Resend failed:', error);
            dialog("エラー", "再送信に失敗しました。");
        } finally {
            setLoading(false);
        }
    };

    // ログイン処理
    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true)
        try {
            const result = await login(
                formData.email,
                formData.password,
                dialog,
                setLoginInfo
            );
            if (result.success) {
                window.location.href = "/"
            } else if (result.requiresTwoFactor) {
                // 2要素認証が必要な場合、2要素認証画面に遷移
                setTwoFactorRequired(true);
                setAntiPhishingPrefix(result.data?.anti_phishing_prefix || '');
                dialog("2要素認証", result.message || "認証コードをメールに送信しました。");
            }
        } catch (error) {
            console.error('Login failed:', error);
        } finally {
            setLoading(false)
        }
    };

    useEffect(() => {
        // タイトル設定
        document.querySelector("title").textContent = t("login.login") + " - アワートAI"
        getImage().then(r => {
            setImage(r.data)
            setCurrentBackground(ApiFetch.API_PROTOCOL + '//' + ApiFetch.IMG_HOST + '/post/t/' + (r.data.id) + "_m" + ApiFetch.API_THUMB_EXT)
        })
    }, i18n.language)

    // 2要素認証が必要になった時にアンチフィッシング接頭辞を更新
    useEffect(() => {
        if (twoFactorRequired && !antiPhishingPrefix) {
            const fetchPrefix = async () => {
                try {
                    const result = await getAntiPhishingPrefix();
                    if (result.result && result.anti_phishing_prefix) {
                        setAntiPhishingPrefix(result.anti_phishing_prefix);
                    }
                } catch (error) {
                    console.error('Failed to fetch anti-phishing prefix:', error);
                }
            };
            fetchPrefix();
        }
    }, [twoFactorRequired, antiPhishingPrefix])

    return (
        <Box sx={styles.wrapper}>
            {/* 背景画像 */}
            <Box
                sx={{
                    ...styles.backgroundImage,
                    backgroundImage: `url(${currentBackground})`,
                }}
            />
            {/* 半透明オーバーレイ */}
            <Box sx={styles.overlay} />
            <Header />

            <Box sx={styles.mainContainer}>
                <Category />
                <Box sx={styles.formContainer}>

                    <Dialog title={dialogTitle} message={dialogMessage} show={show} handleClose={() => setShow(false)} />

                    <Paper
                        elevation={3}
                        sx={styles.paper}
                    >
                        {!twoFactorRequired ? (
                            <>
                                <Typography component="h1" variant="h4" gutterBottom>
                                    {t("login.login")}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    アカウントにログインしてサービスをご利用ください
                                </Typography>

                                <Box component="form" onSubmit={handleSubmit} sx={styles.form}>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="email"
                                        name="email"
                                        label={t("common.emailOrUserId")}
                                        autoComplete="email"
                                        autoFocus
                                        value={formData.email}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="password"
                                        name="password"
                                        label={t("common.password")}
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="current-password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock color="action" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Box sx={styles.forgotPassword}>
                                        <Link
                                            href="/temporary_password"
                                            variant="body2"
                                            underline="hover"
                                        >
                                            パスワードを忘れた場合
                                        </Link>
                                    </Box>

                                    <Button
                                        disabled={loading}
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        sx={styles.submit}
                                    >
                                        ログイン
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <>
                                <Typography component="h1" variant="h4" gutterBottom>
                                    2要素認証
                                </Typography>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    メールに送信された認証コードを入力してください
                                </Typography>
                                
                                {antiPhishingPrefix && (
                                    <Box sx={{ mb: 2, p: 2, bgcolor: 'lightblue', borderRadius: 1 }}>
                                        <Typography variant="body2" color="primary">
                                            フィッシング防止コード: <strong>{antiPhishingPrefix}</strong>
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            このコードがメールの内容と一致していることを確認してください
                                        </Typography>
                                    </Box>
                                )}

                                <Box component="form" onSubmit={handleTwoFactorSubmit} sx={styles.form}>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="twoFactorCode"
                                        name="twoFactorCode"
                                        label="認証コード"
                                        autoComplete="off"
                                        autoFocus
                                        value={twoFactorCode}
                                        onChange={(e) => setTwoFactorCode(e.target.value)}
                                        inputProps={{ maxLength: 6, pattern: '[0-9]{6}' }}
                                    />

                                    <Button
                                        disabled={loading}
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        sx={styles.submit}
                                    >
                                        認証
                                    </Button>
                                    
                                    <Button
                                        disabled={loading}
                                        onClick={handleResendCode}
                                        fullWidth
                                        variant="outlined"
                                        sx={{ mt: 1 }}
                                    >
                                        コードを再送信
                                    </Button>
                                    
                                    <Button
                                        onClick={() => {
                                            setTwoFactorRequired(false);
                                            setTwoFactorCode('');
                                            setAntiPhishingPrefix('');
                                        }}
                                        fullWidth
                                        variant="text"
                                        sx={{ mt: 1 }}
                                    >
                                        ログイン画面に戻る
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Paper>
                </Box>
            </Box>
            <Footer />
        </Box>
    );
};

export default Login;